/**
 * Filter state for Tag mapping, kept out of the menu component so that file
 * exports only a component — same reason matchTone.ts sits beside MatchBadge
 * (the repo's catalogRowOrder.ts / saveEligibility.ts pattern).
 */

/** Server-side facets. `''` means "no filter" — a radio group needs a value. */
export interface TagMappingFilters {
  method: '' | 'auto' | 'manual'
  confidence: '' | 'strong' | 'medium' | 'weak'
  documentId: string
  /** Where the pending tag was read from — a drawing or a document's prose. */
  source: '' | 'sheet' | 'document'
  /**
   * Which KIND of row to show on Matched (Putra, 2026-09-17, P0).
   *
   * The column only ever holds two values there. A match row's type comes from
   * `_ts_documents`, which joins `timeseries_tags` alone, so every match reads
   * `timeseries`; the only `document` rows are the evidence lines a tag's prose
   * mentions produce. So this facet separates matches from document evidence —
   * which is also the answer to "why are we merging items here?" asked in the
   * same thread.
   *
   * Applied to the RENDERED ROWS, not the query: pagination is over source
   * tags and both kinds arrive inside the same group, so there is nothing to
   * narrow server-side and no page boundary to get wrong.
   */
  sourceType: '' | 'timeseries' | 'document'
  /**
   * A board to scope the page to (FS-688). Applies on BOTH tabs, unlike every
   * other facet here: it is a question about which files are in view, not about
   * what a match or a pending tag looks like.
   */
  boardId: string
}

export const EMPTY_FILTERS: TagMappingFilters = {
  method: '',
  confidence: '',
  documentId: '',
  source: '',
  sourceType: '',
  boardId: '',
}

/** One board the page can be scoped to — the shape the Filter menu needs. */
export interface BoardOption {
  id: string
  name: string
}

/**
 * The facets active on a tab, in the Filter menu's order — Pending has no
 * confidence or method, and the board applies to both and leads.
 *
 * One list for the badge AND the chip row (Tag Mapping review 2026-09-28,
 * F20): the count on the Filter button and the chips under it must agree
 * about what is on, so both read this.
 */
export const activeFacets = (
  filters: TagMappingFilters,
  tab: 'matched' | 'pending',
): (keyof TagMappingFilters)[] => {
  const facets: (keyof TagMappingFilters)[] = []
  if (filters.boardId) facets.push('boardId')
  if (tab === 'matched') {
    if (filters.method) facets.push('method')
    if (filters.sourceType) facets.push('sourceType')
    if (filters.confidence) facets.push('confidence')
  } else {
    if (filters.source) facets.push('source')
    if (filters.documentId) facets.push('documentId')
  }
  return facets
}

/** How many facets the visible tab applies — the Filter button's badge. */
export const activeFilterCount = (
  filters: TagMappingFilters,
  tab: 'matched' | 'pending',
): number => activeFacets(filters, tab).length

/**
 * Whether a SERVER facet narrows the visible tab. Source type is left out: it
 * is applied to the rendered rows, and MatchedTable already explains an empty
 * page under it. This is what turns "No matched tags yet." into "No mappings
 * match these filters." (review D1).
 */
export const narrowedByServerFacet = (
  filters: TagMappingFilters,
  tab: 'matched' | 'pending',
): boolean => activeFacets(filters, tab).some((facet) => facet !== 'sourceType')

/**
 * Whether the board is the ONLY server facet narrowing the tab.
 *
 * The page's board-empty state ("No mapped tags on the files in <board>", with
 * a drop-the-board action) is a claim about the board, so it may only show
 * when nothing else could have emptied the page. Board + Confidence → Weak
 * returning nothing is a filter result, not an empty board, and dropping the
 * board would leave the page just as empty (Greptile on #35073). Source type
 * is render-only and cannot empty a server page, so it does not count.
 */
export const boardIsOnlyNarrowing = (
  filters: TagMappingFilters,
  tab: 'matched' | 'pending',
): boolean => {
  const facets = activeFacets(filters, tab)
  return facets.includes('boardId') && facets.every((f) => f === 'boardId' || f === 'sourceType')
}

/** Restore only supported facets; invalid query values must not become hidden filters. */
export const filtersFromSearch = (search: URLSearchParams): TagMappingFilters => {
  const choice = <T extends string>(key: string, allowed: readonly T[]): T | '' => {
    const value = search.get(key)
    return allowed.includes(value as T) ? value as T : ''
  }
  return {
    boardId: search.get('board') || '',
    documentId: search.get('document') || '',
    method: choice('method', ['auto', 'manual']),
    confidence: choice('confidence', ['strong', 'medium', 'weak']),
    source: choice('source', ['sheet', 'document']),
    sourceType: choice('sourceType', ['timeseries', 'document']),
  }
}
